import { useMemo, useState } from 'react';

import {
  CoverageCategory,
  type CoverageListItem,
  CoverageStatus,
} from '@/services/types/coverage-types';

// Hook for managing rejected coverage visibility and filtering
export const useCoverageFiltering = (coverages: CoverageListItem[]) => {
  const [showRejected, setShowRejected] = useState(false);

  const filteredCoverages = useMemo(() => {
    return coverages.filter((coverage) => {
      if (!showRejected && coverage.status === CoverageStatus.REJECTED) {
        return false;
      }
      return true;
    });
  }, [coverages, showRejected]);

  const rejectedCount = useMemo(() => {
    return coverages.filter(
      (coverage) => coverage.status === CoverageStatus.REJECTED,
    ).length;
  }, [coverages]);

  return {
    filteredCoverages,
    rejectedCount,
    showRejected,
    setShowRejected,
  };
};

// Hook for grouping coverages by category with optional filtering
export const useCoverageGrouping = (
  coverages: CoverageListItem[],
  filterCategory?: CoverageCategory,
) => {
  const groupedCoverages = useMemo(() => {
    return coverages.reduce(
      (acc, coverage) => {
        const category = coverage.category || 'UNCATEGORIZED';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(coverage);
        return acc;
      },
      {} as Record<string, CoverageListItem[]>,
    );
  }, [coverages]);

  const filteredGroups = useMemo(() => {
    return filterCategory
      ? { [filterCategory]: groupedCoverages[filterCategory] || [] }
      : groupedCoverages;
  }, [groupedCoverages, filterCategory]);

  return {
    groupedCoverages,
    filteredGroups,
  };
};

// Combined hook for coverage list logic
export const useCoverageListLogic = (
  coverages: CoverageListItem[],
  filterCategory?: CoverageCategory,
) => {
  const filteringState = useCoverageFiltering(coverages);
  const groupingState = useCoverageGrouping(
    filteringState.filteredCoverages,
    filterCategory,
  );

  return {
    ...filteringState,
    ...groupingState,
  };
};
